// src/pages/reports/WastageReport.jsx
import { useState, useEffect, useMemo } from 'react'
import { ChevronDown } from 'lucide-react'
import BaseReportPage from '../../components/reports/BaseReportPage'
import BranchService from '../../services/branchService'
import AuthService from '../../services/authService'

const WastageReportFilters = ({ filters, onFiltersChange, onClear }) => {
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(false)

  // Get user role and branch
  const user = AuthService.getCurrentUser()
  const isManager = user?.role === 'manager'
  const userBranchId = user?.selectedBranch?._id || user?.branchId || user?.branch?._id

  // Initialize manager's branch filter on mount
  useEffect(() => {
    if (isManager && userBranchId && onFiltersChange) {
      const initialFilters = {
        ...filters,
        branch: userBranchId
      }
      console.log('🔧 Manager detected - Setting initial branch filter:', userBranchId)
      onFiltersChange(initialFilters)
    }
  }, [isManager, userBranchId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load branches from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        console.log('🔄 WastageReportFilters: Loading filter data...')
        
        // Load branches
        const branchResponse = await BranchService.getAllBranches()
        console.log('📊 WastageReportFilters: Branch API response:', branchResponse)
        
        if (branchResponse.success && branchResponse.data) {
          const branchesData = branchResponse.data.branches || branchResponse.data
          console.log('🏢 WastageReportFilters: Setting branches:', branchesData)
          setBranches(Array.isArray(branchesData) ? branchesData : [])
        } else {
          console.error('❌ WastageReportFilters: Invalid branch response:', branchResponse)
          setBranches([])
        }
      } catch (error) {
        console.error('❌ WastageReportFilters: Error loading data:', error)
        setBranches([])
      } finally {
        setLoading(false)
        console.log('✅ WastageReportFilters: Data loading complete')
      }
    }

    loadData()
  }, [])

  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value })
  }

  const handleClearFilters = () => {
    console.log('🗑️ WastageReportFilters: Clear All clicked')
    onFiltersChange({
      search: '',
      branch: isManager ? userBranchId : '',
      startDate: '',
      endDate: ''
    })
    onClear()
  }

  const hasActiveFilters = useMemo(() => {
    const active = Boolean(
      (filters?.search && filters.search !== '') || 
      (!isManager && filters?.branch && filters.branch !== '') || 
      (filters?.startDate && filters.startDate !== '') || 
      (filters?.endDate && filters.endDate !== '')
    )
    console.log('🔍 hasActiveFilters check:', { filters, active })
    return active
  }, [filters, isManager])

  return (
    <div className="bg-gray-50 p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="text-xs text-orange-600 hover:text-orange-700 font-medium flex items-center space-x-1"
          >
            <span>Clear All</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Search Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search
          </label>
          <input
            type="text"
            placeholder="Search..."
            value={filters?.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
            disabled={loading}
          />
        </div>

        {/* Branch Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Branch
          </label>
          <div className="relative">
            {isManager ? (
              <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm text-gray-700 cursor-not-allowed">
                {branches.find(b => b._id === userBranchId)?.name || 'Your Branch'}
              </div>
            ) : (
              <>
                <select
                  value={filters?.branch || ''}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  className="w-full appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  disabled={loading}
                >
                  <option value="">All Branches</option>
                  {branches.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-2 h-4 w-4 text-gray-400 pointer-events-none" />
              </>
            )}
          </div>
          {/* {isManager && (
            <p className="text-xs text-gray-500 mt-1">
              🔒 Managers can only view data from their assigned branch
            </p>
          )} */}
        </div>

        {/* Start Date Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <input
            type="date"
            value={filters?.startDate || ''}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
            disabled={loading}
          />
        </div>

        {/* End Date Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            End Date
          </label>
          <input
            type="date"
            value={filters?.endDate || ''}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
            disabled={loading}
          />
        </div>
      </div>

      {loading && (
        <div className="mt-4 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
          <span className="ml-2 text-sm text-gray-600">Loading filters...</span>
        </div>
      )}
    </div>
  )
}

const WastageReport = () => {
  // Define columns for the wastage report
  const columns = [
    { key: 'date', label: 'Date', sortable: true, type: 'date' },
    { key: 'products', label: 'Products', sortable: true, type: 'text' },
    { key: 'supplierWastages', label: 'Supplier Wastages', sortable: true, type: 'currency' },
    { key: 'customerWastages', label: 'Customer Wastages', sortable: true, type: 'currency' },
    { key: 'totalWastage', label: 'Total Wastage', sortable: true, type: 'currency' },
    { key: 'restockStatus', label: 'Stock Restocked?', sortable: true, type: 'text' },
    { key: 'branch', label: 'Branch', sortable: true, type: 'text' },
    { key: 'reasons', label: 'Reasons', sortable: true, type: 'text' }
  ]

  return (
    <BaseReportPage
      title="Wastage Report"
      description="Track and analyze wastages from supplier and customer returns grouped by date"
      reportType="wastage"
      columns={columns}
      customFilterComponent={WastageReportFilters}
      showExport={true}
      showPrint={true}
    />
  )
}

export default WastageReport
